// Campo de competência do módulo de desembolso (design D8): texto com máscara `MM/AAAA` e
// teclado numérico no lugar do `input[type=month]`. O controle nativo é desenhado pelo
// navegador e não obedece ao `lang` — num navegador em inglês o cliente via "November
// 2026" —, então o mês e o ano são sempre numéricos aqui, em qualquer idioma.
//
// O contrato com quem usa é o mesmo do campo nativo: `value` e `onChange` falam a
// competência da API (`"AAAA-MM"`, ou `""` quando o campo pode ficar vazio), nunca o
// rótulo. O que o cliente digita mora num rascunho local; só uma competência completa e
// válida (mês de 01 a 12, ano de quatro dígitos, dentro de `min`/`max`) é propagada. Uma
// digitação inválida mostra o erro e o valor anterior continua valendo em quem usa.
import { useEffect, useId, useState } from "react";

import { competenceOfLabel, labelOfCompetence } from "./months";

const digitsOf = (text) => String(text ?? "").replace(/\D/g, "").slice(0, 6);

/** Dígitos → `"MM"`, `"MM/A"`… `"MM/AAAA"`: a barra entra sozinha depois do mês. */
const maskOf = (digits) =>
  digits.length <= 2 ? digits : `${digits.slice(0, 2)}/${digits.slice(2)}`;

const draftOf = (competence) => labelOfCompetence(competence) ?? "";

/**
 * Lê o rascunho: `{ competence }` quando é uma competência válida, `{ empty: true }` sem
 * nada digitado, `{ incomplete: true }` no meio da digitação e `{ error }` quando o que já
 * foi digitado não tem como virar uma competência.
 */
export const parseCompetenceDraft = (draft, { min, max } = {}) => {
  const digits = digitsOf(draft);
  if (!digits) return { empty: true };
  if (digits.length >= 2) {
    const month = Number(digits.slice(0, 2));
    if (month < 1 || month > 12) return { error: "Mês inválido: use de 01 a 12." };
  }
  if (digits.length < 6) return { incomplete: true };
  const competence = competenceOfLabel(maskOf(digits));
  if (!competence) return { error: "Use o formato MM/AAAA." };
  if (min && competence < min)
    return { error: `A competência não pode ser anterior a ${labelOfCompetence(min)}.` };
  if (max && competence > max)
    return { error: `A competência não pode ser posterior a ${labelOfCompetence(max)}.` };
  return { competence };
};

const CompetenceInput = ({
  value,
  onChange,
  min,
  max,
  allowEmpty = false,
  disabled = false,
  className = "default-input",
  "aria-label": ariaLabel,
  "aria-describedby": describedBy,
  id,
}) => {
  const errorId = useId();
  const [draft, setDraft] = useState(() => draftOf(value));
  const [error, setError] = useState(null);

  // Valor trocado de fora (outro alvo, início sugerido, período do contexto): o rascunho
  // passa a mostrar o novo valor. Enquanto o rascunho já representa o valor, nada muda —
  // senão a máscara "piscaria" a cada render do pai.
  useEffect(() => {
    setDraft((current) => {
      const parsed = parseCompetenceDraft(current);
      if ((parsed.competence ?? "") === (value ?? "")) return current;
      return draftOf(value);
    });
    setError(null);
  }, [value]);

  const handleChange = (event) => {
    const next = maskOf(digitsOf(event.target.value));
    setDraft(next);
    const parsed = parseCompetenceDraft(next, { min, max });
    if (parsed.error) {
      setError(parsed.error);
      return;
    }
    setError(null);
    if (parsed.competence) {
      if (parsed.competence !== value) onChange?.(parsed.competence);
    } else if (parsed.empty && allowEmpty && value) {
      onChange?.("");
    }
  };

  // Só ao sair do campo uma digitação pela metade vira erro: no meio dela o cliente ainda
  // está escrevendo o ano.
  const handleBlur = () => {
    const parsed = parseCompetenceDraft(draft, { min, max });
    if (parsed.incomplete) setError("Use o formato MM/AAAA.");
    else if (parsed.empty && !allowEmpty) setError("Informe a competência (MM/AAAA).");
  };

  const describedByIds = [describedBy, error ? errorId : null].filter(Boolean).join(" ");

  return (
    <>
      <input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder="MM/AAAA"
        maxLength={7}
        id={id}
        className={`${className} ampli-competence-input`.trim()}
        aria-label={ariaLabel}
        aria-describedby={describedByIds || undefined}
        aria-invalid={error ? true : undefined}
        value={draft}
        disabled={disabled}
        onChange={handleChange}
        onBlur={handleBlur}
      />
      {error ? (
        <span className="ampli-competence-input__error error-message" id={errorId} role="alert">
          {error}
        </span>
      ) : null}
    </>
  );
};

export default CompetenceInput;
